import { relativeTime } from "./format";
import type { AgentLinkInfo, AgentWithContext } from "./types";

/**
 * A foreign agent is Claude Code on someone's own machine, reached through the
 * `board-link` CLI holding a socket to the board. These are the words and colours
 * every place that shows one agrees on — the avatar's dot, the list row, the chat
 * header, Settings — kept pure so they are tested once.
 */

export type LinkPresence = "unpaired" | "online" | "offline";

type LinkedAgent = Pick<AgentWithContext, "kind" | "link">;

export const isForeignAgent = (agent: Partial<LinkedAgent> | null | undefined): boolean => agent?.kind === "foreign";

export function linkPresence(link: AgentLinkInfo | null | undefined): LinkPresence {
  if (!link || !link.paired) return "unpaired";
  return link.online ? "online" : "offline";
}

/** Green for a machine connected now, muted otherwise: the avatar dot and the presence lines. */
export const presenceColor = (presence: LinkPresence): string =>
  presence === "online" ? "var(--kind-done)" : "var(--muted-foreground)";

/**
 * Every foreign agent's presence as one string (`id=online,id=offline`), which is what
 * the shared hook selects from the agents query: a string compares by value, so the
 * many avatars reading it re-render only when a foreign agent comes or goes, not on
 * every status change of every agent.
 */
export function foreignPresenceKey(agents: readonly (LinkedAgent & { id: string })[]): string {
  return agents
    .filter((agent) => agent.kind === "foreign")
    .map((agent) => `${agent.id}=${linkPresence(agent.link)}`)
    .join(",");
}

export function parseForeignPresenceKey(key: string): Map<string, LinkPresence> {
  const map = new Map<string, LinkPresence>();
  for (const part of key.split(",")) {
    const [id, presence] = part.split("=");
    if (id && (presence === "online" || presence === "offline" || presence === "unpaired")) map.set(id, presence);
  }
  return map;
}

/** What the machine is called, or a stand-in before it has said. */
export const machineName = (link: AgentLinkInfo | null | undefined): string => link?.machine?.trim() || "its machine";

/** The newest moment the machine was heard from: its heartbeat, else the last disconnect or connect. */
export function lastSeenAt(link: AgentLinkInfo | null | undefined): string | null {
  if (!link) return null;
  const times = [link.seenAt, link.disconnectedAt, link.connectedAt].filter((value): value is string => Boolean(value));
  if (times.length === 0) return null;
  return times.reduce((newest, value) => (new Date(value).getTime() > new Date(newest).getTime() ? value : newest));
}

/** "online" / "offline · last seen 5m ago" / "offline" / "not paired yet". */
export function presenceLabel(link: AgentLinkInfo | null | undefined, now = Date.now()): string {
  const presence = linkPresence(link);
  if (presence === "unpaired") return "not paired yet";
  if (presence === "online") return "online";
  const seen = lastSeenAt(link);
  return seen ? `offline · last seen ${relativeTime(seen, now)}` : "offline";
}

/** "on studio-mbp", or "not paired yet" — what a list row says where a project path would be. */
export function whereLabel(link: AgentLinkInfo | null | undefined): string {
  if (!link?.paired) return "not paired yet";
  return link.machine?.trim() ? `on ${link.machine.trim()}` : "on its machine";
}

/** Turns queued for a machine that is not connected: they run when it reconnects. */
export function waitingForMachine(agent: LinkedAgent & Pick<AgentWithContext, "pendingTurns">): number {
  if (agent.kind !== "foreign" || agent.pendingTurns <= 0) return 0;
  return linkPresence(agent.link) === "online" ? 0 : agent.pendingTurns;
}

/** The banner above the composer while messages wait for the machine. */
export function waitingBannerText(count: number, machine: string): string {
  return count === 1
    ? `1 message waiting for ${machine} — it runs when board-link reconnects.`
    : `${count} messages waiting for ${machine} — they run when board-link reconnects.`;
}

/** Seconds left as "9:42"; null once the moment has passed. */
export function countdown(expiresAt: string, now = Date.now()): string | null {
  const left = Math.ceil((new Date(expiresAt).getTime() - now) / 1000);
  if (!(left > 0)) return null;
  const minutes = Math.floor(left / 60);
  const seconds = left % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/** The three commands the pairing screen shows, for a board at `origin` (e.g. `https://board.example.com`). */
export function pairingCommands(origin: string, code: string, dir = "~/code/my-project"): string[] {
  const base = origin.replace(/\/+$/, "");
  return [
    `curl -fsSL ${base}/link/install.sh | sh`,
    `board-link pair ${code} --url ${base}`,
    `board-link run --dir ${dir}`,
  ];
}

/** What the pairing screen says as the link moves on: waiting for the code, paired, online. */
export type PairingStage = "waiting" | "expired" | "paired" | "online";

/**
 * Where a pairing stands. `issuedAt` is when this screen's code was made: a link that was
 * already paired before it (a re-pair) only counts once it connected after that moment.
 */
export function pairingStage(
  link: AgentLinkInfo | null | undefined,
  expiresAt: string,
  issuedAt: string,
  now = Date.now(),
): PairingStage {
  const issued = new Date(issuedAt).getTime();
  const fresh = Boolean(link?.paired && link.connectedAt && new Date(link.connectedAt).getTime() >= issued - 5_000);
  const live = countdown(expiresAt, now) !== null;
  // `pair` consumes the code: a paired link with no code pending, while ours is still live, redeemed it.
  // (The server also drops an expired code, so past expiry the same reading means nothing.)
  const redeemed = live && Boolean(link?.paired && link.pairingExpiresAt === null);
  if (link?.online && (fresh || redeemed)) return "online";
  if (fresh || redeemed) return "paired";
  return live ? "waiting" : "expired";
}
